import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Loader2, Mail, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useLanguage } from '../contexts/LanguageContext';

const API = process.env.REACT_APP_BACKEND_URL;

const COPY = {
  en: {
    title: 'Forgot password?',
    subtitle: "Enter your email and we'll send you a reset link.",
    emailLabel: 'Email',
    submit: 'Send reset link',
    backToLogin: 'Back to sign in',
    successTitle: 'Check your inbox',
    successBody: "If an account exists for this email, you'll receive a reset link in the next few minutes.",
  },
  bg: {
    title: 'Забравена парола?',
    subtitle: 'Въведи имейла си и ще ти изпратим линк за смяна.',
    emailLabel: 'Имейл',
    submit: 'Изпрати линк',
    backToLogin: 'Обратно към вход',
    successTitle: 'Провери имейла си',
    successBody: 'Ако имаме акаунт с този имейл, ще получиш линк за смяна в следващите минути.',
  },
  es: {
    title: '¿Olvidaste tu contraseña?',
    subtitle: 'Introduce tu correo y te enviaremos un enlace para restablecerla.',
    emailLabel: 'Correo',
    submit: 'Enviar enlace',
    backToLogin: 'Volver a iniciar sesión',
    successTitle: 'Revisa tu bandeja de entrada',
    successBody: 'Si existe una cuenta, recibirás un enlace en los próximos minutos.',
  },
  de: {
    title: 'Passwort vergessen?',
    subtitle: 'Gib deine E-Mail ein und wir senden dir einen Reset-Link.',
    emailLabel: 'E-Mail',
    submit: 'Reset-Link senden',
    backToLogin: 'Zurück zur Anmeldung',
    successTitle: 'Prüfe dein Postfach',
    successBody: 'Falls ein Konto existiert, erhältst du in wenigen Minuten einen Link.',
  },
};

export default function ForgotPasswordPage() {
  const { language } = useLanguage();
  const c = COPY[language] || COPY.en;
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      await axios.post(`${API}/api/auth/forgot-password`, {
        email: email.trim().toLowerCase(),
        language: language || 'en',
      });
      setSent(true);
    } catch (err) {
      // Backend always returns 200 — but be defensive
      toast.error(err?.response?.data?.detail || 'Something went wrong. Try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-background">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md rounded-2xl border border-border bg-card p-8 shadow-lg"
        data-testid="forgot-password-card"
      >
        {sent ? (
          <div className="text-center space-y-3" data-testid="forgot-password-success">
            <CheckCircle2 className="w-10 h-10 text-primary mx-auto" />
            <h1 className="font-heading text-2xl font-bold">{c.successTitle}</h1>
            <p className="text-muted-foreground text-sm">{c.successBody}</p>
            <Link
              to="/login"
              className="inline-flex items-center gap-1.5 text-primary hover:underline text-sm pt-2"
              data-testid="back-to-login-link"
            >
              <ArrowLeft className="w-4 h-4" />
              {c.backToLogin}
            </Link>
          </div>
        ) : (
          <>
            <h1 className="font-heading text-2xl font-bold mb-2">{c.title}</h1>
            <p className="text-muted-foreground text-sm mb-6">{c.subtitle}</p>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1.5" htmlFor="fp-email">
                  {c.emailLabel}
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="fp-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="pl-9 h-11"
                    data-testid="forgot-email-input"
                    autoFocus
                  />
                </div>
              </div>
              <Button
                type="submit"
                disabled={loading || !email}
                className="w-full h-11 rounded-full"
                data-testid="forgot-submit-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : c.submit}
              </Button>
            </form>
            <div className="mt-6 text-center">
              <Link
                to="/login"
                className="inline-flex items-center gap-1.5 text-muted-foreground hover:text-primary text-sm"
                data-testid="back-to-login-link"
              >
                <ArrowLeft className="w-4 h-4" />
                {c.backToLogin}
              </Link>
            </div>
          </>
        )}
      </motion.div>
    </div>
  );
}
